import { StrategyBuilderFunction, EMPTY_STRATEGY } from './types';
import { addLongCall, addShortCall, addLongPut, addShortPut } from './addOption';
import {
  getDefaultExpiration,
  findClosestStrikePrice,
  getStrikesAtExpiration,
  expirationToDate,
  getPremium,
  getStrategyLegStrikeDirection,
  getStrikeDistance,
} from './utils';

export const doubleDiagonal: StrategyBuilderFunction = ({
  chain,
  spot,
  strikeDirection,
  contractMultiplier = 1,
}) => {
  // Setup for the long-term options (buy positions)
  const longTermExpiry = getDefaultExpiration(chain, 2);

  // Setup for the short-term options (sell positions)
  const shortTermExpiry = getDefaultExpiration(chain);

  if (
    !longTermExpiry ||
    !longTermExpiry.e ||
    !longTermExpiry.ey ||
    !longTermExpiry.em ||
    !longTermExpiry.ed
  ) {
    return EMPTY_STRATEGY;
  }
  if (
    !shortTermExpiry ||
    !shortTermExpiry.e ||
    !shortTermExpiry.ey ||
    !shortTermExpiry.em ||
    !shortTermExpiry.ed
  ) {
    return EMPTY_STRATEGY;
  }

  const longTermStrikes = longTermExpiry ? getStrikesAtExpiration(chain, longTermExpiry.e) : null;

  const shortTermStrikes = shortTermExpiry
    ? getStrikesAtExpiration(chain, shortTermExpiry.e)
    : null;

  if (!longTermStrikes || !shortTermStrikes) {
    return EMPTY_STRATEGY;
  }

  const putLowerStrike = findClosestStrikePrice(
    longTermStrikes,
    getStrikeDistance({ price: spot, unit: -3 }),
  );
  const putHigherStrike = findClosestStrikePrice(
    shortTermStrikes,
    getStrikeDistance({ price: spot, unit: -1 }),
  );
  const callLowerStrike = findClosestStrikePrice(
    shortTermStrikes,
    getStrikeDistance({ price: spot, unit: 1 }),
  );
  const callHigherStrike = findClosestStrikePrice(
    longTermStrikes,
    getStrikeDistance({ price: spot, unit: 3 }),
  );

  if (
    callLowerStrike === -1 ||
    callHigherStrike === -1 ||
    putLowerStrike === -1 ||
    putHigherStrike === -1
  ) {
    return EMPTY_STRATEGY;
  }

  // Expiration dates
  const longExpirationDate = longTermExpiry
    ? expirationToDate(longTermExpiry.ey, longTermExpiry.em, longTermExpiry.ed)
    : null;
  const shortExpirationDate = shortTermExpiry
    ? expirationToDate(shortTermExpiry.ey, shortTermExpiry.em, shortTermExpiry.ed)
    : null;

  if (!longExpirationDate || !shortExpirationDate) {
    return EMPTY_STRATEGY;
  }

  return {
    optionPositions: [
      // Long Call at higher strike (long-term)
      addLongCall({
        strike: callHigherStrike,
        premium: getPremium({
          ask: longTermStrikes?.c?.[callHigherStrike]?.c?.a || 0,
          bid: longTermStrikes?.c?.[callHigherStrike]?.c?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
        }),
        expirationDate: longExpirationDate,
        expirationKey: longTermExpiry.e,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Short Call at lower strike (short-term)
      addShortCall({
        strike: callLowerStrike,
        premium: getPremium({
          ask: shortTermStrikes?.c?.[callLowerStrike]?.c?.a || 0,
          bid: shortTermStrikes?.c?.[callLowerStrike]?.c?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'short'),
        }),
        expirationDate: shortExpirationDate,
        expirationKey: shortTermExpiry.e,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Long Put at lower strike (long-term)
      addLongPut({
        strike: putLowerStrike,
        premium: getPremium({
          ask: longTermStrikes?.c?.[putLowerStrike]?.p?.a || 0,
          bid: longTermStrikes?.c?.[putLowerStrike]?.p?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
        }),
        expirationDate: longExpirationDate,
        expirationKey: longTermExpiry.e,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Short Put at higher strike (short-term)
      addShortPut({
        strike: putHigherStrike,
        premium: getPremium({
          ask: shortTermStrikes?.c?.[putHigherStrike]?.p?.a || 0,
          bid: shortTermStrikes?.c?.[putHigherStrike]?.p?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'short'),
        }),
        expirationDate: shortExpirationDate,
        expirationKey: shortTermExpiry.e,
        numberOfContracts: 1 * contractMultiplier,
      }),
    ],
    stockPositions: [],
  };
};
